<template>
  <div class="h-100">
    <Layout ref="layout">
      <template v-slot:body>
        <div class="cmp-page">
          <h3>Comparação</h3>
          <p class="cmp-help">
            Compare, hora a hora, dispositivos do mesmo grupo. Os grupos são configurados pela equipe
            OnAgro; fale com o suporte para criar ou alterar um grupo.
          </p>

          <div class="cmp-filtros">
            <div class="cmp-filtro">
              <label class="form-label">Grupo</label>
              <select class="form-select" v-model="grupoId" @change="onGrupoChange">
                <option v-if="!gruposOpcoes.length" :value="null" disabled>Nenhum grupo</option>
                <option v-for="g in gruposOpcoes" :key="g.id" :value="g.id">{{ g.nome }}</option>
              </select>
            </div>
            <div class="cmp-filtro">
              <label class="form-label">Dia</label>
              <Datepicker
                v-model="data"
                :locale="ptLocale"
                minimumView="day"
                dayFormat="dd"
                inputFormat="dd/MM/yyyy"
                :upperLimit="hoje"
                auto-close
                style="width: 100%; height: 38px;"
              />
            </div>
          </div>

          <div v-if="erro" class="cmp-aviso">{{ erro }}</div>
          <div v-else-if="loading" class="cmp-aviso">Carregando dados…</div>

          <template v-else-if="grupo">
            <div v-if="grupo.dispositivos.length < 2" class="cmp-aviso">
              Este grupo tem só um dispositivo que você pode ver. Inclua outros no cadastro dos dispositivos.
            </div>

            <!-- Atalhos: liga/desliga a métrica em todos os dispositivos de uma vez -->
            <div v-if="metricasDisponiveis.length" class="cmp-atalhos">
              <span class="cmp-atalhos-label">Métricas:</span>
              <button v-for="m in metricasDisponiveis" :key="m.nome" type="button"
                :class="['cmp-chip', { ativo: m.estado === 'todas', parcial: m.estado === 'parcial' }]"
                :title="m.estado === 'todas' ? 'Desligar em todos' : 'Ligar em todos'"
                @click="alternarMetrica(m)">
                {{ m.nome }}
              </button>
              <button type="button" class="cmp-limpar" :disabled="!selecionadas.length" @click="selecionadas = []">
                Limpar
              </button>
            </div>

            <div class="cmp-grid">
              <!-- Séries disponíveis, por dispositivo -->
              <div class="cmp-series">
                <div v-for="d in dispositivosComSeries" :key="d.qrCodeId" class="cmp-dispositivo">
                  <label class="cmp-dispositivo-nome">
                    <input v-if="d.series.length" type="checkbox"
                      :checked="estadoDispositivo(d) === 'todas'"
                      :indeterminate.prop="estadoDispositivo(d) === 'parcial'"
                      @change="alternarDispositivo(d)" />
                    {{ d.nome }}
                  </label>
                  <div v-if="!d.series.length" class="cmp-vazio">Sem leituras neste dia.</div>
                  <label v-for="s in d.series" :key="s.id" class="cmp-serie">
                    <input type="checkbox" :value="s.id" v-model="selecionadas" />
                    <span class="cmp-cor" :style="{ background: s.color }"></span>
                    <span>{{ s.metrica }} <small>({{ s.unit }})</small></span>
                  </label>
                </div>
              </div>

              <!-- Gráfico + tabela -->
              <div class="cmp-resultado">
                <div v-if="!seriesSelecionadas.length" class="cmp-aviso">Selecione ao menos uma série.</div>
                <template v-else>
                  <!-- Um gráfico por unidade: vazão, volume, temperatura… cada um com um
                       eixo só, em vez de vários eixos espremidos no mesmo gráfico. -->
                  <div v-for="g in graficosPorUnidade" :key="g.unit" class="cmp-card">
                    <div class="cmp-grafico-titulo">{{ g.titulo }} <small>{{ g.unit }}</small></div>
                    <div class="cmp-legenda">
                      <span v-for="s in g.series" :key="s.id" class="cmp-legenda-item">
                        <span class="cmp-cor" :style="{ background: s.color }"></span>{{ s.legenda }}
                      </span>
                    </div>
                    <ComparacaoChart :series="g.series" />
                  </div>
                  <div class="cmp-card cmp-tabela-wrapper">
                    <table class="table table-sm cmp-tabela">
                      <!-- Cabeçalho em 2 níveis: dispositivo em cima (agrupando as colunas
                           dele), métrica embaixo — o nome não se repete em cada coluna. -->
                      <thead>
                        <tr>
                          <th rowspan="2" class="cmp-col-hora">Hora</th>
                          <th v-for="g in colunasPorDispositivo" :key="g.qrCodeId" :colspan="g.series.length"
                            class="cmp-th-dispositivo cmp-inicio-grupo">
                            {{ g.nome }}
                          </th>
                        </tr>
                        <tr>
                          <th v-for="s in seriesSelecionadas" :key="s.id"
                            :class="['cmp-th-metrica', { 'cmp-inicio-grupo': inicioDeGrupo.has(s.id) }]">
                            <span class="cmp-cor" :style="{ background: s.color }"></span>
                            {{ s.metrica }} <small>{{ s.unit }}</small>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="h in horasComDado" :key="h">
                          <td class="cmp-col-hora">{{ String(h).padStart(2, '0') }}:00</td>
                          <td v-for="s in seriesSelecionadas" :key="s.id"
                            :class="['cmp-num', { 'cmp-inicio-grupo': inicioDeGrupo.has(s.id) }]">
                            {{ formatar(s.values[h], s.decimals) }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </template>
              </div>
            </div>
          </template>
        </div>
      </template>
    </Layout>
  </div>
</template>

<script>
import Layout from "../../components/layout/Tema.vue";
import Datepicker from "vue3-datepicker";
import { datepickerLocale } from "../../mixin/datepickerLocale";
import ComparacaoChart from "./ComparacaoChart.vue";

/**
 * Métricas que cada tipo de sensor oferece na comparação. `get` lê uma hora da
 * resposta de /Station/GetSensorStats (ou GetStationHourlyStats, para estação).
 * Sensor que não está aqui simplesmente não aparece.
 */
const avg = (field) => (h) => h[field]?.avg ?? null;
const TEMP = { key: "temperature", metrica: "Temperatura", unit: "°C", get: avg("temperature") };
const UMID = { key: "humidity", metrica: "Umidade", unit: "%", get: avg("humidity") };
const TANQUES = [1, 2, 3, 4, 5, 6].map((n) => ({
  key: `temp${n}`, metrica: `Tanque ${n}`, unit: "°C", get: avg(`temp${n}`),
  // Tanque sem sonda chega como 0 o dia inteiro: não vira série.
  zeroEhVazio: true, padrao: true,
}));
const SONDA = [{ key: "depthCalc", metrica: "Profundidade", unit: "m", get: avg("depthCalc"), padrao: true }];

const CATALOGO = {
  estacao: [
    { ...TEMP, padrao: true },
    UMID,
    { key: "pressure", metrica: "Pressão", unit: "hPa", get: avg("pressure") },
    { key: "windSpeed", metrica: "Vento", unit: "km/h", get: avg("windSpeed") },
    { key: "gustSpeed", metrica: "Rajada", unit: "km/h", get: avg("gustSpeed") },
    { key: "rainfall", metrica: "Chuva", unit: "mm", get: (h) => h.rainfall ?? null },
  ],
  ritter: [
    { key: "vazao", metrica: "Vazão", unit: "L/h", get: avg("vazao"), padrao: true, decimals: 3 },
    // Volume do dia é acumulado: o valor da hora é o máximo lido nela.
    { key: "volume", metrica: "Volume do dia", unit: "L", get: (h) => h.volumeHoje?.max ?? null },
    TEMP,
    { key: "pressao", metrica: "Pressão", unit: "hPa", get: avg("pressao") },
  ],
  fotobio: TANQUES,
  lut: [{ key: "lux", metrica: "Luminosidade", unit: "lx", get: avg("lux"), decimals: 0 }, { ...TEMP, padrao: true }, UMID],
  solo: [{ ...TEMP, padrao: true }, UMID],
  folhar: [{ ...TEMP, padrao: true }, UMID],
  ambiente: [{ ...TEMP, padrao: true }, UMID],
  "sonda-hidrostatica": SONDA,
  "sonda-laser": SONDA,
  "sonda-nivel": SONDA,
  "sonda-caixa": SONDA,
};

const CORES = ["#E74C3C", "#2E86DE", "#10AC84", "#F98601", "#9B59B6", "#1ABC9C", "#C0392B",
  "#495948", "#E91E63", "#7D3C98", "#148F77", "#795548", "#3E97FF", "#FBC02D"];

const isoDia = (d) => {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};

export default {
  name: "Comparacao",
  components: { Layout, Datepicker, ComparacaoChart },
  data() {
    const q = this.$route.query;
    return {
      ptLocale: datepickerLocale,
      hoje: new Date(),
      grupoId: q.grupo ? Number(q.grupo) : null,
      data: q.date ? new Date(`${q.date}T12:00:00`) : new Date(),
      meusGrupos: [],
      grupo: null,
      series: [],
      selecionadas: [],
      // Seleção já feita pelo usuário: trocar de dia não deve resetar as escolhas.
      selecaoInicializada: false,
      loading: false,
      erro: null,
      requisicao: 0,
    };
  },
  computed: {
    // Grupos do usuário + o grupo aberto pelo link do aside (pode ser de outro dono).
    gruposOpcoes() {
      const lista = [...this.meusGrupos];
      if (this.grupo && !lista.some((g) => g.id === this.grupo.id)) lista.unshift(this.grupo);
      return lista;
    },
    dispositivosComSeries() {
      if (!this.grupo) return [];
      return this.grupo.dispositivos.map((d) => ({
        ...d,
        series: this.series.filter((s) => s.qrCodeId === d.qrCodeId),
      }));
    },
    seriesSelecionadas() {
      const sel = new Set(this.selecionadas);
      return this.series.filter((s) => sel.has(s.id));
    },
    // Colunas selecionadas agrupadas por dispositivo, na ordem em que aparecem.
    colunasPorDispositivo() {
      const grupos = [];
      this.seriesSelecionadas.forEach((s) => {
        const ultimo = grupos[grupos.length - 1];
        if (ultimo && ultimo.qrCodeId === s.qrCodeId) ultimo.series.push(s);
        else grupos.push({ qrCodeId: s.qrCodeId, nome: this.nomeDispositivo(s.qrCodeId), series: [s] });
      });
      return grupos;
    },
    // Primeira coluna de cada dispositivo: recebe a borda que separa os grupos.
    inicioDeGrupo() {
      return new Set(this.colunasPorDispositivo.map((g) => g.series[0].id));
    },
    // Métricas presentes no dia (por nome: "Pressão" junta a da estação e a do Ritter).
    metricasDisponiveis() {
      const sel = new Set(this.selecionadas);
      const porNome = new Map();
      this.series.forEach((s) => {
        if (!porNome.has(s.metricaBase)) porNome.set(s.metricaBase, []);
        porNome.get(s.metricaBase).push(s.id);
      });
      return [...porNome.entries()].map(([nome, ids]) => {
        const marcadas = ids.filter((id) => sel.has(id)).length;
        const estado = marcadas === 0 ? "nenhuma" : marcadas === ids.length ? "todas" : "parcial";
        return { nome, ids, estado };
      });
    },
    graficosPorUnidade() {
      const porUnidade = new Map();
      this.seriesSelecionadas.forEach((s) => {
        if (!porUnidade.has(s.unit)) porUnidade.set(s.unit, []);
        porUnidade.get(s.unit).push(s);
      });
      return [...porUnidade.entries()].map(([unit, series]) => {
        const metricas = [...new Set(series.map((s) => s.metricaBase))];
        // Com uma métrica só no gráfico, o título já diz qual é: a legenda fica só com o dispositivo.
        const legendaCurta = metricas.length === 1;
        return {
          unit,
          titulo: metricas.join(" / "),
          series: series.map((s) => ({
            ...s,
            legenda: legendaCurta ? this.nomeDispositivo(s.qrCodeId) : s.label,
          })),
        };
      });
    },
    horasComDado() {
      const horas = [];
      for (let h = 0; h < 24; h++) {
        if (this.seriesSelecionadas.some((s) => s.values[h] != null)) horas.push(h);
      }
      return horas;
    },
  },
  watch: {
    data() { this.carregarDados(); },
  },
  async mounted() {
    try {
      const meus = await this.urlGet("/GrupoComparacao/Visiveis");
      this.meusGrupos = meus.data || [];
    } catch (e) {
      this.meusGrupos = [];
    }
    if (this.grupoId == null && this.meusGrupos.length) this.grupoId = this.meusGrupos[0].id;
    if (this.grupoId == null) {
      this.erro = "Nenhum grupo de comparação com dispositivos seus. Fale com o suporte para configurar um.";
      return;
    }
    this.carregarGrupo();
  },
  methods: {
    onGrupoChange() {
      this.selecaoInicializada = false;
      this.$router.replace({ query: { ...this.$route.query, grupo: this.grupoId } });
      this.carregarGrupo();
    },
    async carregarGrupo() {
      this.erro = null;
      this.grupo = null;
      this.series = [];
      try {
        const r = await this.urlGet(`/GrupoComparacao/${this.grupoId}`);
        this.grupo = r.data;
      } catch (e) {
        this.erro = e?.response?.status === 404
          ? "Grupo não encontrado ou sem dispositivos que você possa ver."
          : "Não foi possível carregar o grupo.";
        return;
      }
      this.carregarDados();
    },
    async carregarDados() {
      if (!this.grupo || !this.data) return;
      const req = ++this.requisicao;
      const dia = isoDia(this.data);
      this.loading = true;
      try {
        const porDispositivo = await Promise.all(
          this.grupo.dispositivos.map((d) => this.seriesDoDispositivo(d, dia).catch(() => []))
        );
        // Resposta de um dia/grupo que já foi trocado: descarta.
        if (req !== this.requisicao) return;

        const series = porDispositivo.flat();
        series.forEach((s, i) => { s.color = CORES[i % CORES.length]; });
        this.series = series;

        if (!this.selecaoInicializada) {
          this.selecionadas = series.filter((s) => s.padrao).map((s) => s.id);
          this.selecaoInicializada = true;
        }
      } finally {
        if (req === this.requisicao) this.loading = false;
      }
    },
    async seriesDoDispositivo(dispositivo, dia) {
      const id = dispositivo.qrCodeId;
      const sensores = (await this.urlPost(`/Station/GetSensorByQrCodeId/${id}`)).data?.itens || [];
      const series = [];

      for (const sensor of sensores) {
        const metricas = CATALOGO[sensor.linkname];
        if (!metricas) continue;

        const sourceIds = sensor.sourceIds && sensor.sourceIds.length ? sensor.sourceIds : [null];
        for (const sourceId of sourceIds) {
          let url = sensor.linkname === "estacao"
            ? `/Station/GetStationHourlyStats/${id}/estacao?date=${dia}`
            : `/Station/GetSensorStats/${id}/${sensor.linkname}?date=${dia}`;
          if (sourceIds.length > 1) url += `&hasSourceIdFilter=true&sourceId=${encodeURIComponent(sourceId)}`;

          let horas;
          try {
            horas = (await this.urlGet(url)).data?.hours || [];
          } catch (e) {
            continue;
          }
          if (!horas.length) continue;

          // Vários sensores do mesmo tipo no dispositivo (ex.: solo em profundidades).
          const origem = sourceIds.length > 1 ? ` [${sourceId === "default" ? "principal" : sourceId}]` : "";
          const nomeSensor = sensores.length > 1 ? ` · ${sensor.nome}` : "";

          for (const m of metricas) {
            const values = Array(24).fill(null);
            horas.forEach((h) => {
              const v = m.get(h);
              if (v != null && h.hour != null) values[h.hour] = Number(v);
            });
            const temDado = values.some((v) => v != null && (!m.zeroEhVazio || v !== 0));
            if (!temDado) continue;

            series.push({
              id: `${id}|${sensor.linkname}|${sourceId}|${m.key}`,
              qrCodeId: id,
              metrica: `${m.metrica}${origem}${nomeSensor}`,
              metricaBase: m.metrica,
              label: `${dispositivo.nome} · ${m.metrica}${origem}`,
              unit: m.unit,
              decimals: m.decimals ?? 2,
              padrao: !!m.padrao,
              values,
            });
          }
        }
      }
      return series;
    },
    alternarMetrica(m) {
      const ids = new Set(m.ids);
      if (m.estado === "todas") {
        this.selecionadas = this.selecionadas.filter((id) => !ids.has(id));
      } else {
        this.selecionadas = [...new Set([...this.selecionadas, ...m.ids])];
      }
    },
    estadoDispositivo(d) {
      const sel = new Set(this.selecionadas);
      const marcadas = d.series.filter((s) => sel.has(s.id)).length;
      if (marcadas === 0) return "nenhuma";
      return marcadas === d.series.length ? "todas" : "parcial";
    },
    alternarDispositivo(d) {
      const ids = d.series.map((s) => s.id);
      if (this.estadoDispositivo(d) === "todas") {
        const remover = new Set(ids);
        this.selecionadas = this.selecionadas.filter((id) => !remover.has(id));
      } else {
        this.selecionadas = [...new Set([...this.selecionadas, ...ids])];
      }
    },
    nomeDispositivo(qrCodeId) {
      return this.grupo?.dispositivos.find((d) => d.qrCodeId === qrCodeId)?.nome || `Dispositivo ${qrCodeId}`;
    },
    formatar(v, casas) {
      if (v == null) return "--";
      return Number(v).toLocaleString("pt-BR", { minimumFractionDigits: casas, maximumFractionDigits: casas });
    },
  },
};
</script>

<style scoped>
.cmp-help {
  color: #777;
  font-size: 14px;
}
.cmp-filtros {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}
.cmp-filtro {
  flex: 0 1 280px;
}
.cmp-aviso {
  background: #fff;
  border-radius: 10px;
  padding: 16px;
  color: #666;
  text-align: center;
  margin-bottom: 12px;
}
.cmp-grid {
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr);
  gap: 16px;
  align-items: start;
}
@media (max-width: 900px) {
  .cmp-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
.cmp-series {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.cmp-dispositivo {
  background: #fff;
  border-radius: 10px;
  padding: 12px;
}
.cmp-dispositivo-nome {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
  margin-bottom: 6px;
  color: #333;
  cursor: pointer;
}
.cmp-atalhos {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
}
.cmp-atalhos-label {
  font-size: 13px;
  color: #666;
  margin-right: 2px;
}
.cmp-chip {
  border: 1px solid #C3D9AD;
  background: #fff;
  color: #333;
  border-radius: 20px;
  padding: 4px 12px;
  font-size: 13px;
  cursor: pointer;
}
.cmp-chip.parcial {
  background: #E8F8E6;
  border-style: dashed;
}
.cmp-chip.ativo {
  background: #006035;
  border-color: #006035;
  color: #fff;
}
.cmp-limpar {
  border: none;
  background: transparent;
  color: #006035;
  font-size: 13px;
  text-decoration: underline;
  cursor: pointer;
}
.cmp-limpar:disabled {
  color: #bbb;
  cursor: default;
  text-decoration: none;
}
.cmp-grafico-titulo {
  font-weight: 700;
  color: #333;
  margin-bottom: 4px;
}
.cmp-grafico-titulo small {
  color: #999;
  font-weight: 400;
}
.cmp-legenda {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  font-size: 12px;
  color: #444;
  margin-bottom: 8px;
}
.cmp-legenda-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.cmp-vazio {
  font-size: 13px;
  color: #999;
}
.cmp-serie {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  padding: 3px 0;
  cursor: pointer;
}
.cmp-serie small {
  color: #999;
}
.cmp-cor {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex: 0 0 auto;
}
.cmp-card {
  background: #fff;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 16px;
}
/* Sem padding no container que rola: a coluna Hora (sticky) gruda na borda interna
   dele, e com padding as outras colunas apareciam passando pela faixa à esquerda. */
.cmp-tabela-wrapper {
  overflow-x: auto;
  padding: 0;
}
.cmp-tabela th:first-child,
.cmp-tabela td:first-child {
  padding-left: 12px;
}
.cmp-tabela th:last-child,
.cmp-tabela td:last-child {
  padding-right: 12px;
}
.cmp-tabela {
  font-size: 13px;
  margin: 0;
  white-space: nowrap;
}
.cmp-tabela th {
  vertical-align: middle;
}
/* Cabeçalho da métrica em caixa normal: o tema deixa th em maiúsculas espaçadas,
   o que dobra a largura das colunas. */
.cmp-th-metrica {
  text-transform: none;
  letter-spacing: normal;
  font-weight: 600;
  color: #555;
  text-align: right;
}
.cmp-th-metrica small {
  color: #999;
  font-weight: 400;
  margin-left: 2px;
}
.cmp-th-dispositivo {
  text-align: center;
  color: #006035;
  border-bottom: 2px solid #C3D9AD;
}
.cmp-inicio-grupo {
  border-left: 2px solid #e3e3e3;
}
.cmp-num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}
/* Hora fica visível ao rolar a tabela para o lado. */
.cmp-col-hora {
  position: sticky;
  left: 0;
  background: #fff;
  z-index: 2;
  /* Linha à direita que acompanha a coluna fixa ao rolar (borda comum some no sticky). */
  box-shadow: inset -1px 0 0 #e3e3e3;
}
</style>
